/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {Avatar} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';

/**
 * AvatarButton is a custom React component that displays an avatar and a username.
 *
 * @param {Object} props - The component props.
 * @param {string} props.username - The username to display.
 * @param {Function} props.handleClick - The click event handler for the component.
 * @returns {JSX.Element} - The rendered component.
 */
export interface AvatarButtonProps {
  username: string;
  handleClick?: Function;
}

const useStyles = makeStyles((theme) => ({
  avatarWrapper : {
    padding        : '8px',
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center',
    cursor         : 'pointer',
    borderRadius   : '4px',
    '&:hover'      : {
      backgroundColor : dlsColor('paleBlue')
    }
  },
  username : {
    ...dlsTypography('body1'),
    marginLeft : '4px',
    padding    : '0px !important',
    whiteSpace : 'nowrap'
  },
  avatarIcon : {
    width           : '20px',
    height          : '20px',
    backgroundColor : 'transparent',
    color           : dlsColor('grey3'),
    border          : `2px solid ${dlsColor('grey3')}`
  }
}));

const AvatarButton = ({username, handleClick}: AvatarButtonProps) => {
  const classes = useStyles();

  function onClick(e) {
    handleClick(e);
  }

  return (
    <div className={classes.avatarWrapper} onClick={onClick}>
      <Avatar className={classes.avatarIcon}/>
      <Typography className={classes.username}
        onClick={onClick}>
        {username}
      </Typography>
    </div>
  );
};

export default AvatarButton;
